import React from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { Image } from '@/atoms/Image'
import { AsH2, CaptionSM, HeadingXS, ParagraphSM } from '@/atoms/Text'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useTranslate } from '@/utils/translate/translate-client'
import { useGuildSignupContext } from '../GuildSignupContext'

interface GuildPredictsWinnersSectionProps {
  landing?: boolean
}

export const GuildPredictsWinnersSection: React.FC<GuildPredictsWinnersSectionProps> = ({ landing = false }) => {
  const { t } = useTranslate('join-the-guild')
  const { guildPredictsWinners } = useGuildSignupContext()

  if (!guildPredictsWinners) return null

  const yearSpan = guildPredictsWinners?.customData[0]?.yearSpan ?? '2024-2025'
  const updatedDate = guildPredictsWinners?.customData[0]?.updatedAt ?? 'January 6, 2025'

  const sortedCompanies = [...(guildPredictsWinners?.customData[0]?.companies ?? [])].sort(
    (a: StatsCompanyReference, b: StatsCompanyReference) => a.score - b.score,
  )

  return (
    <section
      className={classNames(
        'flex flex-col items-center justify-center py-16 md:py-28 w-full',
        landing ? 'bg-core-gray-950 text-white' : 'bg-core-gray-100 text-core-gray-950',
      )}
      id="guild-collection"
      aria-labelledby="guild-predicts-winners-title"
    >
      <div
        className={classNames(
          'mx-8 flex w-fit flex-col items-start gap-1 md:mx-4 md:flex-row md:gap-8',
          landing && 'md:flex-col items-center text-center',
        )}
      >
        <div>
          <Image
            src={getCloudinaryImageUrl({
              path: landing
                ? '/v1705528511/angel-studios/guild/Guild_Angel_Copper.webp'
                : '/v1700075329/angel-studios/guild/Version_Guild_Angel_Copper_OnLight_Yes.webp',
              width: landing ? 71 : 90,
              height: landing ? 88 : 112,
            })}
            width={landing ? 71 : 90}
            height={landing ? 88 : 112}
            alt={t('angelGuildAlt', 'Angel Guild')}
            loading="lazy"
            className={classNames(
              'mx-auto',
              landing ? 'mb-0 w-[60px] md:w-[65px] lg:w-[71px]' : 'mb-4 w-[60px] md:w-[70px] lg:w-[82px]',
            )}
          />
        </div>
        <div className="flex flex-col gap-y-2">
          <HeadingXS
            as={AsH2}
            weight="bold"
            id="guild-predicts-winners-title"
            className="md:photon-heading-sm lg:photon-heading-md"
          >
            {t('guildPredictsWinners', 'Angel Guild Predicts Winners')}
          </HeadingXS>
          <ParagraphSM
            color={landing ? 'core-gray-400' : 'core-gray-800'}
            className={classNames('md:photon-paragraph-md', landing ? 'mx-auto' : 'max-w-[400px]')}
          >
            {t(
              'averageRottenTomatoesPopcornmeter',
              'Average Rotten Tomatoes Popcornmeter for wide releases by studio for {{ yearSpan }}.',
              { yearSpan },
            )}
          </ParagraphSM>
        </div>
      </div>
      <When condition={sortedCompanies?.length > 0}>
        <>
          <div className="mt-8 flex flex-row gap-2 border-b border-solid border-core-gray-400 px-4 md:gap-4 lg:gap-6">
            {sortedCompanies.map((item: StatsCompanyReference, index: React.Key) => (
              <div key={index} className="flex flex-col items-center justify-end">
                <Image
                  src={
                    item.imageUrl.includes('.svg')
                      ? item.imageUrl
                      : getCloudinaryImageUrl({
                          path: item.imageUrl,
                          width: 50,
                        })
                  }
                  width={50}
                  height={50}
                  alt={item.name}
                  loading="lazy"
                  className={classNames('mb-2 max-w-[45px] md:max-w-[50px] se:max-w-[40px]', landing && 'invert')}
                />
                <div
                  className={classNames('w-10 md:w-12 lg:w-14 flex justify-center pt-1', {
                    [landing ? 'bg-core-gray-800' : 'bg-core-gray-950']: true,
                    'bg-oxide': item.name === 'Angel',
                  })}
                  style={{ height: `${item.score * 1.5}px` }}
                >
                  <CaptionSM weight="semibold" color="white">{`${item.score}%`}</CaptionSM>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-row items-center justify-center gap-2 md:gap-4">
            <Image
              width={65}
              height={19}
              src={getCloudinaryImageUrl({
                path: landing
                  ? '/co_white,e_colorize:100/v1741120453/angel-studios/guild/predicts-winners/image_37.webp'
                  : '/co_rgb:565453,e_colorize:100/v1741120453/angel-studios/guild/predicts-winners/image_37.webp',
                width: 65,
                height: 19,
              })}
              alt={'Rotten Tomatoes Logo'}
              loading="lazy"
              className="mb-2"
            />
            <CaptionSM color={landing ? 'core-gray-400' : 'black'}>
              {t('asOfDate', 'As of {{ updatedDate }}', { updatedDate })}
            </CaptionSM>
          </div>
        </>
      </When>
    </section>
  )
}

interface StatsCompanyReference {
  imageUrl: string
  name: string
  score: number
}
